import React, { useEffect, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { PartnerId } from '../../common/types';
import TravelSearchBar from '../../components/TravelSearchBar';
import { IPartner } from '../../contexts/App/types';
import { useSearch } from '../../contexts/Search';
import { ISearchContext } from '../../contexts/Search/types';
import { searchHeaderBackground } from '../../data/home';
import { isMobileApp } from '../../helpers/app';
import NodiHandTop from '../../images/illustrations/nodi-hand-1.png';
import NodiHandBottom from '../../images/illustrations/nodi-hand-2.png';
import { screens } from '../../styles/constants';
import messages from '../TravelSearchBar/View/messages';
import InitialHeader from './View/InitialHeader';

interface ContainerProps {
  partner: IPartner;
  isMobileApp: boolean;
  isSearchBarOpen: boolean;
  imageUrl?: string;
}

const Container = styled.section<ContainerProps>`
  ${tw`relative px-medium2 py-big `}

  ${({ partner }) =>
    partner && partner.primaryColor
      ? css`
          background-color: ${partner.primaryColor};
        `
      : css`
          background: linear-gradient(180deg, #22e3aa -21.69%, #f5f4ec 59.86%);
        `}
  

  @media (min-width: ${screens.md}) {
    ${({ imageUrl, isSearchBarOpen, partner }) =>
      partner && partner.backgroundUrl
        ? css`
            background: linear-gradient(
                rgba(53, 55, 70, 0.3),
                rgba(53, 55, 70, 0.3)
              ),
              url(${partner.backgroundUrl});

            background-position: bottom;
          `
        : css`
            background: ${isSearchBarOpen &&
              `linear-gradient(
              rgba(53, 55, 70, 0.3),
              rgba(53, 55, 70, 0.3)
            ),`}
              ${imageUrl
                ? `url(${imageUrl})`
                : ` image-set(
              url(${searchHeaderBackground.images['1x']}) 1x,
              url(${searchHeaderBackground.images['2x']}) 2x,
              url(${searchHeaderBackground.images['3x']}) 3x
            );`};

            background-position: ${imageUrl ? 'center' : 'bottom'};
          `}

    background-size: cover;
  }

  @media (min-width: ${screens.md}) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: var(--size-medium2);
    grid-row-gap: var(--size-medium2);
    padding: var(--size-huge) 24px;
  }

  @media (min-width: ${screens.lg}) {
    padding: var(--size-huge) 116px;
  }

  ${({ isMobileApp }) =>
    isMobileApp &&
    css`
      flex: 1;
    `}

  ${({ isMobileApp, partner }) =>
    !isMobileApp &&
    !partner &&
    css`
      @media (min-width: 1200px) {
        &:before {
          position: absolute;
          content: url(${NodiHandTop});
          top: 0;
          left: 0;
        }

        &:after {
          position: absolute;
          content: url(${NodiHandBottom});
          bottom: -95px;
          left: 0;
        }
      }
    `}
`;

const SearchBarWrapper = styled.div`
  ${tw`mb-medium 
    md:mb-0
  `}

  @media (min-width: ${screens.md}) {
    grid-column-start: 1;
    grid-column-end: 6;
  }
`;

const ContentWrapper = styled.div`
  ${tw``}

  @media (min-width: ${screens.md}) {
    grid-column-start: 6;
    grid-column-end: 12;
  }
`;

const TravelSearchBarPanelStyle = css`
  @media (min-width: ${screens.md}) {
    position: absolute;
    grid-column-start: 6;
    grid-column-end: 13;
    grid-row-start: 1;
    grid-row-end: 1;
  }
`;

export type ISearchHeaderProps = {
  header?: React.ReactNode;
  subHeader?: React.ReactNode;
  partner?: IPartner;

  defaultOutboundDate?: string;
  defaultReturnDate?: string;
  defaultDepartureCity?: any;
  defaultArrivalCity?: any;
  setIsTravelSearchBarPanelOpen: (isOpen: boolean) => void;
  imageUrl?: string;
};

const SearchHeader = ({
  defaultArrivalCity,
  defaultDepartureCity,
  defaultOutboundDate,
  defaultReturnDate,
  header,
  imageUrl,
  partner,
  setIsTravelSearchBarPanelOpen,
  subHeader,
}: ISearchHeaderProps) => {
  const searchContext: ISearchContext = useSearch();

  const isCdiscount = partner?.id === PartnerId.WB_CDISCOUNT;
  const isMobileAppTest: boolean = isMobileApp(navigator.userAgent);
  const [isTSBPanelOpen, setIsTSBPanelOpen] = useState<boolean>(false);

  function onTravelSearchBarChange(isOpen: boolean) {
    setIsTSBPanelOpen(isOpen);
    setIsTravelSearchBarPanelOpen?.(isOpen);
  }

  useEffect(() => {
    if (
      defaultOutboundDate ||
      defaultArrivalCity ||
      defaultReturnDate ||
      defaultDepartureCity ||
      defaultOutboundDate
    ) {
      searchContext.setSearchParameters((searchParameters) => ({
        ...searchParameters,
        ...(defaultOutboundDate && { outboundDate: defaultOutboundDate }),
        ...(defaultReturnDate && { returnDate: defaultReturnDate }),
        ...(defaultArrivalCity && {
          destination: defaultArrivalCity.name,
          destinationId: defaultArrivalCity.id,
        }),
        ...(defaultDepartureCity && {
          origin: defaultDepartureCity.name,
          originId: defaultDepartureCity.id,
        }),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      imageUrl={imageUrl}
      isMobileApp={isMobileAppTest}
      isSearchBarOpen={isTSBPanelOpen}
      partner={partner}
    >
      <SearchBarWrapper>
        <TravelSearchBar
          onPanelChange={onTravelSearchBarChange}
          panelStyle={TravelSearchBarPanelStyle}
          searchContext={searchContext}
        />
      </SearchBarWrapper>

      <ContentWrapper>
        <InitialHeader
          header={
            header ||
            (isCdiscount ? (
              <FormattedMessage {...messages.cdiscountHeader} />
            ) : (
              <FormattedMessage {...messages.header} />
            ))
          }
          isCdiscount={isCdiscount}
          isMobileApp={isMobileAppTest}
          subHeader={
            subHeader ||
            (isCdiscount ? (
              <FormattedHTMLMessage {...messages.cdiscountSubHeader} />
            ) : (
              <FormattedHTMLMessage {...messages.subHeader} />
            ))
          }
          withBackground={(partner && !!partner.backgroundUrl) || !!imageUrl}
        />
      </ContentWrapper>
    </Container>
  );
};

export default SearchHeader;
